import { useState } from "react"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { Prompt } from "@molecules/index"

import useTable from "@organisms/Table/Table.context"

import { getCategoryTabTitles } from "@pages/Catalogs/BrandItems/BrandItems.utils"
import {
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"
import { SubscribePayload } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.types"

import { SubscribeScopeButtonProps } from "./SubscribeScopeButton.types"

export default function SubscribeScopeButton(props: SubscribeScopeButtonProps) {
    const { catalogId, brandId, brandName, categories } = props

    const { activeTab } = useTable()
    const subscriptionsQuery = useCatalogSubscriptionsQuery(Number(catalogId))
    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isUnbranded = brandId === ""
    const brandIdNumber = Number(brandId)

    // Category tabs carry an id of `category_<id>`; matching on it (rather than the tab title)
    // stays correct even though the tab title carries the nearest ancestor's name as a prefix.
    const activeCategory = categories.find((category) => activeTab?.id === `category_${category.id}`)
    const categoryTabTitles = getCategoryTabTitles(categories)
    const activeCategoryTitle = activeCategory
        ? (categoryTabTitles.get(activeCategory.id) ?? activeCategory.name)
        : undefined

    const scopeLabel = isUnbranded
        ? activeCategoryTitle
            ? `No brand / ${activeCategoryTitle}`
            : "items without a brand"
        : activeCategoryTitle
          ? `${brandName} / ${activeCategoryTitle}`
          : brandName

    // The active scope is "subscribed" when its exact-level subscription exists, or when a broader
    // one already covers it (catalog level covers everything; brand/bucket level covers its categories).
    // The unbranded "bucket" is a brand-level subscription with a null brand.
    const subscriptions = subscriptionsQuery.data ?? []
    const exactSubscription = activeCategory
        ? subscriptions.find(
              (subscription) =>
                  subscription.level === "category" &&
                  subscription.category === activeCategory.id &&
                  (isUnbranded ? subscription.brand === null : subscription.brand === brandIdNumber),
          )
        : subscriptions.find(
              (subscription) =>
                  subscription.level === "brand" &&
                  (isUnbranded ? subscription.brand === null : subscription.brand === brandIdNumber),
          )
    const isCoveredByBroader = subscriptions.some(
        (subscription) =>
            subscription.level === "catalog" ||
            (activeCategory !== undefined &&
                subscription.level === "brand" &&
                (isUnbranded ? subscription.brand === null : subscription.brand === brandIdNumber)),
    )

    const subscribePayload: SubscribePayload = activeCategory
        ? isUnbranded
            ? { catalog: Number(catalogId), level: "category", category: activeCategory.id }
            : { catalog: Number(catalogId), level: "category", brand: brandIdNumber, category: activeCategory.id }
        : isUnbranded
          ? { catalog: Number(catalogId), level: "brand" }
          : { catalog: Number(catalogId), level: "brand", brand: brandIdNumber }

    const subscribeButtonLabel = activeCategory
        ? "Subscribe to category"
        : isUnbranded
          ? "Subscribe to these items"
          : "Subscribe to brand"

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate(subscribePayload)
    }

    // The subscribe/unsubscribe mutations invalidate the shared subscriptions cache themselves, so the
    // badge and the item rows both re-derive without this page refetching anything.
    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate(subscribePayload)
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        if (exactSubscription) {
            unsubscribeMutation.mutate(exactSubscription)
        }
    }

    const previewCount = previewCountMutation.data?.count
    const ongoingImportClause = activeCategory
        ? "new items added to this category later will be imported automatically too"
        : isUnbranded
          ? "new items added without a brand later will be imported automatically too"
          : "new items added to this brand later will be imported automatically too"
    const promptBody =
        previewCount === undefined
            ? `This will import the covered items into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`
            : `This will import ${previewCount} ${previewCount === 1 ? "item" : "items"} into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`

    if (exactSubscription || isCoveredByBroader) {
        return (
            <>
                <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                    Subscribed
                </AlertBadge>
                {exactSubscription && (
                    <Button
                        variant="subtle"
                        size="sm"
                        onClick={() => setIsUnsubscribePromptOpen(true)}
                        isProcessing={unsubscribeMutation.isPending}
                        isDisabled={unsubscribeMutation.isPending}
                    >
                        Unsubscribe
                    </Button>
                )}

                <Prompt
                    title={`Unsubscribe from ${scopeLabel}?`}
                    body="Items already in your pricebook will stay there, but they will no longer receive catalog updates, and new items in this scope will no longer be imported automatically."
                    isOpen={isUnsubscribePromptOpen}
                    confirmText="Unsubscribe"
                    onConfirm={handleUnsubscribeConfirm}
                    cancelText="Cancel"
                    onCancel={() => setIsUnsubscribePromptOpen(false)}
                    isDangerousOperation={true}
                />
            </>
        )
    }

    return (
        <>
            <Button
                variant="solid"
                size="sm"
                leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                onClick={handleSubscribeClick}
                isProcessing={subscribeMutation.isPending}
                isDisabled={subscribeMutation.isPending}
            >
                {subscribeButtonLabel}
            </Button>

            <Prompt
                title={`Subscribe to ${scopeLabel}?`}
                body={promptBody}
                isOpen={isSubscribePromptOpen}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />
        </>
    )
}
